import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, FileText, Loader2, RefreshCw, ScanBarcode } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/api/client';
import PDFViewerDialog from '@/components/mail/PDFViewerDialog';

const when = (value) => formatInTimeZone(new Date(value), 'America/New_York', 'MMM d, h:mm a');

const STATUS = {
  created: { label: 'Ready', variant: 'secondary' },
  pending: { label: 'Being prepared', variant: 'outline' },
  failed: { label: 'Failed', variant: 'destructive' },
};

/**
 * USPS SCAN forms.
 *
 * One sheet with one barcode, in place of the carrier scanning every parcel at
 * pickup. Only labels bought through EasyPost are eligible, and a form cannot
 * be changed once it exists - a label forgotten is a second form, not an edit -
 * so the page says so before the button is pressed rather than after.
 */
export default function ScanFormsPanel() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(() => new Set());
  const [viewing, setViewing] = useState(null);

  const { data: eligible = [], isLoading: eligibleLoading } = useQuery({
    queryKey: ['scanFormEligible'],
    queryFn: () => api.request('/easypost/scan-forms/eligible'),
  });

  const { data: forms = [], isLoading: formsLoading } = useQuery({
    queryKey: ['scanForms'],
    queryFn: () => api.request('/easypost/scan-forms'),
  });

  const allSelected = eligible.length > 0 && selected.size === eligible.length;

  // EasyPost refuses a form whose labels ship from different addresses. Names
  // stand in for addresses here, so this warns rather than blocks.
  const origins = new Set(
    eligible.filter((label) => selected.has(label.id)).map((label) => label.returnAddressName),
  );

  const toggle = (id) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const afterChange = () => {
    queryClient.invalidateQueries({ queryKey: ['scanFormEligible'] });
    queryClient.invalidateQueries({ queryKey: ['scanForms'] });
    queryClient.invalidateQueries({ queryKey: ['mailLabels'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      api.request('/easypost/scan-forms', { method: 'POST', body: { labelIds: [...selected] } }),
    onSuccess: (form) => {
      setSelected(new Set());
      afterChange();

      if (form.status === 'failed') toast.error(form.message ?? 'The scan form could not be made');
      else if (form.status === 'pending') toast.info('Scan form requested. It will appear here when USPS has it ready.');
      else toast.success(`Scan form made for ${form.labelCount} label${form.labelCount === 1 ? '' : 's'}`);
    },
    onError: (error) => toast.error(error.message ?? 'Could not make the scan form'),
  });

  const refreshMutation = useMutation({
    mutationFn: (form) => api.request(`/easypost/scan-forms/${form.id}/refresh`, { method: 'POST' }),
    onSuccess: afterChange,
    onError: (error) => toast.error(error.message ?? 'Could not check on the scan form'),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardTitle className="flex items-center gap-2">
            <ScanBarcode className="w-5 h-5" />
            Make a scan form
          </CardTitle>
        </CardHeader>

        <CardContent className="p-6 space-y-4">
          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Choose every label going out in this pickup. A scan form cannot be changed once it is
              made, and a label can be on only one. Labels must all ship from the same address.
            </AlertDescription>
          </Alert>

          {eligibleLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          ) : eligible.length === 0 ? (
            <p className="text-sm text-slate-500">
              No labels are waiting. USPS labels bought through EasyPost in the last two weeks
              appear here until they are on a scan form.
            </p>
          ) : (
            <>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(checked) =>
                    setSelected(checked === true ? new Set(eligible.map((l) => l.id)) : new Set())
                  }
                />
                Select all ({eligible.length})
              </label>

              <ul className="divide-y rounded-lg border max-h-96 overflow-y-auto">
                {eligible.map((label) => (
                  <li key={label.id}>
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <Checkbox
                        checked={selected.has(label.id)}
                        onCheckedChange={() => toggle(label.id)}
                        className="mt-1"
                      />
                      <span className="min-w-0 text-sm">
                        <span className="font-medium text-slate-900">{label.recipientName}</span>
                        <span className="text-slate-500">
                          {' '}
                          — {label.recipientCity}, {label.recipientState}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {label.serviceName} · {when(label.createdAt)} · from {label.returnAddressName}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>

              {origins.size > 1 && (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    These labels ship from {origins.size} different addresses ({[...origins].join(', ')}).
                    A scan form covers one address, so make a separate form for each.
                  </AlertDescription>
                </Alert>
              )}

              <Button
                className="w-full"
                disabled={selected.size === 0 || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Making scan form…
                  </>
                ) : (
                  `Make scan form for ${selected.size} label${selected.size === 1 ? '' : 's'}`
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Scan forms made
          </CardTitle>
        </CardHeader>

        <CardContent className="p-6">
          {formsLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          ) : forms.length === 0 ? (
            <p className="text-sm text-slate-500">None yet.</p>
          ) : (
            <ul className="space-y-3">
              {forms.map((form) => {
                const status = STATUS[form.status] ?? { label: form.status, variant: 'outline' };

                return (
                  <li key={form.id} className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm">
                        <p className="font-medium text-slate-900">
                          {form.labelCount} label{form.labelCount === 1 ? '' : 's'}
                        </p>
                        <p className="text-xs text-slate-500">{when(form.createdAt)}</p>
                      </div>
                      <Badge variant={status.variant}>{status.label}</Badge>
                    </div>

                    {form.status === 'failed' && form.message && (
                      <p className="text-xs text-red-700">{form.message}</p>
                    )}

                    <div className="flex gap-2">
                      {form.status === 'created' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setViewing({
                              url: `/api/documents/scan-forms/${form.id}/form`,
                              title: `Scan form — ${when(form.createdAt)}`,
                            })
                          }
                        >
                          View / print
                        </Button>
                      )}
                      {form.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2"
                          disabled={refreshMutation.isPending}
                          onClick={() => refreshMutation.mutate(form)}
                        >
                          <RefreshCw className="w-4 h-4" />
                          Check now
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {viewing && (
        <PDFViewerDialog
          open
          onClose={() => setViewing(null)}
          documentUrl={viewing.url}
          title={viewing.title}
        />
      )}
    </div>
  );
}
